import type { Grossmedium } from '../../api/types'
import { groesse } from '../../lib/format'
import type { Texte } from '../../texte'
import { ladeblatt } from '../../components/Rueckfrage'
import { brauchtEigeneGeste, dateiname, fetchFile, handOverAll, type Weg } from '../../lib/sichern'

/** The sheet while fetching and before handing on, worded by the caller. */
export interface SaveMediaWords {
  loading: string
  /** "3,2 von 9,1 MB" -- `gesamt` is `null` while the size is unknown. */
  progress: (geladen: number, gesamt: number | null) => string
  ready: string
  button: string
}

/** The words for one video or several files -- one place for the menu and the large view. */
export function saveMediaWords(t: Texte, count: number): SaveMediaWords {
  const one = count === 1
  return {
    loading: one ? t.chat.videoWirdGeladen : t.chat.medienWirdGeladen(count),
    progress: (geladen, gesamt) => gesamt === null
      ? t.chat.ladenOhneGesamt(groesse(geladen))
      : t.chat.ladenStand(groesse(geladen), groesse(gesamt)),
    ready: one ? t.chat.videoBereit : t.chat.medienBereit(count),
    button: t.chat.videoBereitKnopf,
  }
}

/**
 * Save one video, or all photos and videos of a post (1.159.0) -- from a
 * bubble's menu and from the large view.
 *
 * From use: "Video lassen sich aktuell nicht herunterladen (bei Fotos gibt
 * es den Knopf: dieses Foto sichern - das sollte es auch für Videos geben)",
 * and then "auch wenn mehrere Bilder / Videos angezeigt werden (4er-Raster)
 * —> Menüeintrag: Fotos und Videos herunterladen". A single photo is fetched
 * and shared in one tap; it is small and usually in the browser's cache
 * already. A video, or several files, take longer than iOS keeps a tap
 * alive: the share sheet opens only inside a tap (`brauchtEigeneGeste()`),
 * after the fetch the tap is gone, the share fails, and the download that
 * follows does nothing in the home-screen app -- the fault of the book in
 * 1.53.0.
 *
 * So on iPhone and iPad a sheet stands between the two, **always**, not only
 * when the fetch was slow: a way that hangs on the clock looks like chance.
 * Its button hands the files on inside its own tap (`beimJa` runs in the
 * click, `handOverAll()` calls the share sheet before any `await`), all of
 * them in one sheet. Elsewhere the downloads need no tap and follow at once.
 *
 * One place for every caller, so the rule cannot run apart.
 */
export async function saveMedia(media: Grossmedium[], words: SaveMediaWords): Promise<Weg> {
  /*
   * **A sheet with a bar from the tap on** (1.160.0, `ladeblatt()`). From
   * use: "es steht unten in der Fußzeile, dass der Download erfolgt.
   * speziell bei langsamer Verbindung dauert es aber, bis der Speichern
   * Dialog erscheint. besser wäre hier auch ein Fenster mit
   * Fortschrittsbalken (mit Möglichkeit abzubrechen)". The total is the sum
   * of the sizes the server named (`bytes`); a medium without one is counted
   * by its `content-length` once it answers.
   */
  const abbruch = new AbortController()
  const blatt = ladeblatt(words.loading, words.progress, () => abbruch.abort(), 'medien-laden')
  const bekannt = media.every((medium) => typeof medium.bytes === 'number' && medium.bytes > 0)
  let vorher = 0
  let gesamt: number | null = bekannt ? media.reduce((summe, medium) => summe + (medium.bytes ?? 0), 0) : null
  const dateien: File[] = []
  try {
    // One after the other: a phone fetching four videos at once holds four
    // of them in memory before the first is done.
    for (const medium of media) {
      const datei = await fetchFile(medium.url, dateiname(medium), medium.mime, {
        signal: abbruch.signal,
        onBytes: (geladen, laenge) => {
          // A single file without a size from the server: its own length.
          if (!bekannt && media.length === 1) gesamt = laenge
          blatt.stand(vorher + geladen, gesamt === null ? null : Math.max(gesamt, vorher + geladen))
        },
      })
      vorher += datei.size
      dateien.push(datei)
    }
  } catch (fehler) {
    blatt.zu()
    if (abbruch.signal.aborted) return 'abgebrochen'
    throw fehler
  }
  if (!brauchtEigeneGeste()) {
    blatt.zu()
    return handOverAll(dateien)
  }

  const handed: { weg?: Promise<Weg> } = {}
  const ja = await blatt.bereit(words.ready, words.button, {
    art: 'normal',
    merkmal: 'medien-sichern',
    beimJa: () => {
      handed.weg = handOverAll(dateien)
    },
  })
  if (!ja || handed.weg === undefined) return 'abgebrochen'
  return handed.weg
}
